import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { difficultySchema } from '@platform/schema/content';

import { personaName, PERSONAS } from '../authoring/personas';

import type { Difficulty, Persona } from '@platform/schema/content';

/**
 * The two labels a reviewer changes without opening a lesson — GEN-70.
 *
 * Both controls exist twice on the lessons screen: in a row, where the column header
 * already says which label it is, and in the bar that changes every selected row, where it
 * does not. Hence `label` optional and `aria-label` not — an unlabelled select in a table
 * reads to a screen reader as one control repeated once per lesson.
 *
 * Why these two columns and no others: `relabelLessonsSchema`.
 */

const LEVELS = difficultySchema.options;

/** Chips a persona set shows before counting the rest — ADMIN-17. */
const SHOWN = 1;
const CHIP_WIDTH = 120;

export function LevelSelect({
  label,
  describedAs,
  value,
  disabled,
  onChange,
}: {
  /** Shown above the control. Absent in a row, where the column header says it. */
  label?: string;
  /** What a screen reader announces. Always present, unlike the visible label. */
  describedAs: string;
  value: Difficulty | '';
  disabled: boolean;
  onChange: (level: Difficulty) => void;
}) {
  return (
    <FormControl size="small" sx={{ minWidth: 130 }}>
      {label !== undefined && <InputLabel>{label}</InputLabel>}
      <Select
        label={label}
        value={value}
        disabled={disabled}
        inputProps={{ 'aria-label': describedAs }}
        onChange={(event) => {
          onChange(difficultySchema.parse(event.target.value));
        }}
      >
        {LEVELS.map((level) => (
          <MenuItem key={level} value={level}>
            {level}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/**
 * Who a lesson is for, as a set.
 *
 * Reports the whole set on every change, for the reason `LabelEditor` does: the write
 * replaces, and an empty set is how the last persona the planner guessed is removed. The
 * grade is deliberately not here — the list shows who, not how well, and the API keeps
 * each lesson's existing grades for the personas that survive the change.
 */
export function PersonaSelect({
  label,
  describedAs,
  value,
  disabled,
  onChange,
}: {
  label?: string;
  describedAs: string;
  value: readonly Persona[];
  disabled: boolean;
  onChange: (personas: Persona[]) => void;
}) {
  // A long set would size its column, so one is drawn and the tooltip names the set —
  // ADMIN-17.
  return (
    <Tooltip title={value.map(personaName).join(', ')} describeChild>
      <FormControl size="small" sx={{ minWidth: 190 }}>
        {/* Always shrunk: `displayEmpty` fills the field, so a resting label overlaps it — ADMIN-21. */}
        {label !== undefined && <InputLabel shrink>{label}</InputLabel>}
        <Select<Persona[]>
          multiple
          label={label}
          notched
          value={[...value]}
          disabled={disabled}
          inputProps={{ 'aria-label': describedAs }}
          displayEmpty
          renderValue={(picked) =>
            picked.length === 0 ? (
              'unlabelled'
            ) : (
              <Stack direction="row" spacing={0.5}>
                {picked.slice(0, SHOWN).map((persona) => (
                  <Chip
                    key={persona}
                    size="small"
                    label={personaName(persona)}
                    sx={{ maxWidth: CHIP_WIDTH }}
                  />
                ))}
                {picked.length > SHOWN && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`+${String(picked.length - SHOWN)}`}
                  />
                )}
              </Stack>
            )
          }
          onChange={(event) => {
            const picked = event.target.value;

            onChange(typeof picked === 'string' ? [] : picked);
          }}
        >
          {PERSONAS.map((persona) => (
            <MenuItem key={persona} value={persona}>
              {personaName(persona)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Tooltip>
  );
}
